import React, { useState, useEffect, useRef } from 'react';
import '../scss/loyalty-promotion-banner.component.scss';
import { useTranslation } from 'react-i18next';

type SlideKey = 'exclusive' | 'platinum' | 'gold';

interface RewardSlide {
    key: SlideKey;
    titleKey: string;
    descKey: string;
    badgeKey: string;
    icon: React.ReactNode;
}

const PlaneIcon = () => (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M21 4 3 11.5l6 2.2L11.2 20 21 4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="m9 13.7 12-9.7-9.8 9.9" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
);

const DiamondIcon = () => (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M6 3.5h12l3.2 5.1-9.2 11.9L2.8 8.6 6 3.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M2.8 8.6h18.4M9 3.5 7.3 8.6 12 20.5 16.7 8.6 15 3.5" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" opacity="0.7" />
    </svg>
);

const TrophyIcon = () => (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M7 4h10v4.5a5 5 0 0 1-10 0V4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M7 5.2H4.4v1.4A3.6 3.6 0 0 0 7 10M17 5.2h2.6v1.4A3.6 3.6 0 0 1 17 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M12 13.3v3m-3.2 4h6.4m-5.4 0 .6-3h3.6l.6 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const SLIDES: RewardSlide[] = [
    {
        key: 'exclusive',
        titleKey: 'LOYALTY_REWARDS_SLIDE_EXCLUSIVE_TITLE',
        descKey: 'LOYALTY_REWARDS_SLIDE_EXCLUSIVE_DESC',
        badgeKey: 'LOYALTY_REWARDS_SLIDE_EXCLUSIVE_BADGE',
        icon: <PlaneIcon />
    },
    {
        key: 'platinum',
        titleKey: 'LOYALTY_REWARDS_SLIDE_PLATINUM_TITLE',
        descKey: 'LOYALTY_REWARDS_SLIDE_PLATINUM_DESC',
        badgeKey: 'LOYALTY_REWARDS_SLIDE_PLATINUM_BADGE',
        icon: <DiamondIcon />
    },
    {
        key: 'gold',
        titleKey: 'LOYALTY_REWARDS_SLIDE_GOLD_TITLE',
        descKey: 'LOYALTY_REWARDS_SLIDE_GOLD_DESC',
        badgeKey: 'LOYALTY_REWARDS_SLIDE_GOLD_BADGE',
        icon: <TrophyIcon />
    }
];

const pad2 = (n: number) => String(n).padStart(2, '0');

const LoyaltyPromotionBanner: React.FC = () => {
    const { t } = useTranslation();
    const [index, setIndex] = useState(0);
    const isPaused = useRef(false);
    const slideCount = SLIDES.length;

    // Auto-advance every 3.5s, paused on hover
    useEffect(() => {
        const id = window.setInterval(() => {
            if (!isPaused.current) {
                setIndex((prev) => (prev + 1) % slideCount);
            }
        }, 3500);
        return () => window.clearInterval(id);
    }, [slideCount]);

    const active = SLIDES[index];

    return (
        <div
            className="loyalty-promotion-banner rewards-carousel"
            onMouseEnter={() => { isPaused.current = true; }}
            onMouseLeave={() => { isPaused.current = false; }}
        >
            {SLIDES.map((slide, i) => (
                <div
                    key={slide.key}
                    className={`rc-slide slide-${slide.key}${i === index ? ' is-active' : ''}`}
                    aria-hidden={i === index ? undefined : true}
                >
                    <span className="rc-orb rc-orb-top" />
                    <span className="rc-orb rc-orb-bottom" />

                    <div className="rc-top">
                        <span className="rc-chip">
                            <span className="rc-chip-dot" />
                            {t('LOYALTY_REWARDS_CAROUSEL_CHIP')}
                        </span>
                        <span className="rc-counter">{pad2(i + 1)} / {pad2(slideCount)}</span>
                    </div>

                    <div className="rc-icon-tile">{slide.icon}</div>

                    <div className="rc-body">
                        <h4 className="rc-title">{t(slide.titleKey)}</h4>
                        <p className="rc-desc">{t(slide.descKey)}</p>
                    </div>

                    <div className="rc-badge-tag">
                        <span className="rc-badge-label">{t('LOYALTY_REWARDS_BADGE_LABEL')}</span>
                        <span className="rc-badge-value">{t(slide.badgeKey)}</span>
                    </div>
                </div>
            ))}

            <div className="rc-footer">
                <div className="rc-dots" role="tablist">
                    {SLIDES.map((slide, i) => (
                        <button
                            key={slide.key}
                            type="button"
                            className={`rc-dot${i === index ? ' active' : ''}`}
                            aria-label={`Go to slide ${i + 1}`}
                            aria-selected={i === index}
                            onClick={() => setIndex(i)}
                        />
                    ))}
                </div>
                <span className="rc-thanks">{t('LOYALTY_REWARDS_CAROUSEL_THANKS')} ✨</span>
            </div>
        </div>
    );
};

export default LoyaltyPromotionBanner;
